import { Box, IconButton, Paper, Typography } from "@mui/material";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import FilterList from "@mui/icons-material/FilterList";
import { useAsyncAction } from "nate-react-api-helpers";
import moment from "moment";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { api } from "../../../api/API";
import { emptyRepeat, Task } from "../../../api/Tasks";
import { Dropdown } from "../../../misc/Dropdown";
import { TinyButton } from "../../../misc/TinyButton";
import { useProjectFeed, useTaskFeed } from "../../../misc/Feed";
import { PaletteGate } from "../../../misc/PaletteGate";
import { SidePanel } from "../../../misc/SidePanel";
import { useSnackAction } from "../../../misc/SnackAction";
import { usePhone } from "../../../misc/usePhone";
import { useWorkspace } from "../../../misc/Workspace";
import { MobileWeekPage } from "../week/MobileWeekPage";
import { usePanels } from "../../panel/usePanels";
import { ProjectStrip } from "../../project/ProjectStrip";
import { BulkTaskPanel } from "../bulk/BulkTaskPanel";
import { TaskChainDialog } from "../chain/TaskChainDialog";
import { calendarOrder } from "../dayGroups";
import { NewTaskButton } from "../NewTaskButton";
import { useTaskContextMenu } from "../TaskContextMenu";
import { CalendarGrid } from "./CalendarGrid";
import { UnscheduledButton } from "./UnscheduledButton";
import { dateFormat, useToday } from "../../../misc/dates";
import { useUrlState } from "../../../misc/urlParams";
import { calendarRange, CalendarView, collapsesDay, DragTask, parseAnchor, parseView, rangeLabel } from "./range";

const viewOptions: { value: CalendarView; label: string }[] = [
    { value: "week", label: "Week" },
    { value: "full-week", label: "Full week" },
    { value: "month", label: "Month" },
];

// TaskCalendarPage is the week or month grid. A phone hasn't the width for it, so it
// gets the week as an agenda instead.
export function TaskCalendarPage() {
    const phone = usePhone();
    return phone ? <MobileWeekPage /> : <CalendarPage />;
}

function CalendarPage() {
    // the url says which range is on screen, and the panel slot reads the rest of it
    const url = useUrlState();
    const view = parseView(url.text("mode"));
    const anchorDate = parseAnchor(url.text("date"));

    const ws = useWorkspace();
    const team = ws.team || undefined;
    const [dragging, setDragging] = useState<DragTask | null>(null);
    const [chain, setChain] = useState(false);

    // the server only knows what zone the reader is in because this says so
    const today = useToday();
    const anchor = moment(anchorDate, dateFormat);
    const range = calendarRange(anchor, view);
    const from = range.start.format(dateFormat);
    const to = range.end.format(dateFormat);

    // the server pushes both lists again after every change, so nothing here reloads
    const list = useTaskFeed({ dueFrom: from, dueTo: to, today: today, includeCompletedChecklist: true, team: team });

    // a task with no due date has no cell to sit in, so it gets a strip of its own
    const unscheduled = useTaskFeed({ unscheduled: true, completed: false, team: team });

    // the due count follows the range on screen, so stepping through the calendar
    // re-reads what each project has left in it
    const projects = useProjectFeed({ archived: false, dueFrom: from, dueTo: to, today: today, team: team });

    // the inline add in a day cell only collects a title; the rest is seeded here, and
    // the server assigns it to the user. The new task isn't opened, so the next title
    // can be typed straight after
    const create = useSnackAction(async (input: { date: string; title: string }) => {
        await api.tasks.upsert({
            type: "task",
            title: input.title,
            description: "",
            dueDate: input.date,
            dueTime: null,
            completedAt: null,
            archived: false,
            repeat: emptyRepeat,
            team: ws.newTeam,
        });
    }, { success: "Task created" });

    const toggle = useSnackAction(async (input: { id: number; completed: boolean }) => {
        await api.tasks.complete(input);
    }, { success: "Saved" });

    // drag-drop edits stay quiet; a failure shows under the header
    const reorder = useAsyncAction(async (input: { ids: number[] | null; task: Task; date: string }) => {
        // a task carried onto the day is only visiting it, so the drop that sorts it
        // into place hands it that day's date to keep
        if (input.task.carriedOver) {
            await api.tasks.reschedule({ id: input.task.id, dueDate: input.date });
        }

        if (input.ids) await api.tasks.reorder({ ids: input.ids });
    }, []);

    // a task that's been ticked off is moved to the day it was in fact done; an open
    // one is moved to a new due date
    const reschedule = useAsyncAction(async (input: { task: Task; date: string }) => {
        if (input.task.completedAt) {
            await api.tasks.complete({
                id: input.task.id,
                completed: true,
                completedOn: input.date,
                // the drop says where the task belongs, so its due date comes too
                moveDueDate: true,
            });
            return;
        }

        await api.tasks.reschedule({ id: input.task.id, dueDate: input.date });
    }, []);

    const dragError = reorder.error || reschedule.error;

    const archive = useSnackAction(async (input: { id: number }) => {
        await api.tasks.archive(input);
    }, { success: "Archived" });

    const moveToday = useSnackAction(async (input: { id: number }) => {
        await api.tasks.reschedule({ id: input.id, dueDate: today });
    }, { success: "Moved to today" });

    const menu = useTaskContextMenu<Task>({
        onArchive: task => archive.callback({ id: task.id }),
        onToggle: (task, completed) => toggle.callback({ id: task.id, completed: completed }),
        onToday: task => moveToday.callback({ id: task.id }),
    });

    // the lists on screen already carry most of what a panel opens, so only a link to
    // something outside them costs a fetch
    const panels = usePanels({
        tasks: list.tasks.concat(unscheduled.tasks),
        projects: projects.projects,
        unscheduled: {
            tasks: unscheduled.tasks,
            saving: archive.loading || moveToday.loading || toggle.loading,
            onContextMenu: menu.open,
            onToggle: (task, checked) => toggle.callback({ id: task.id, completed: checked }),
            onDragStart: task => setDragging({ task: task, date: "" }),
            onDragEnd: () => setDragging(null),
        },
    });

    // the tasks picked for a bulk change, and the task a shift-click selects a range from
    const [selected, setSelected] = useState<number[]>([]);
    const [rangeFrom, setRangeFrom] = useState(0);
    const selectedTasks = list.tasks.filter(task => selected.includes(task.id));
    const bulk = selectedTasks.length > 1;

    // a click opens the task. A shift-click selects the range from the last task
    // clicked, and a cmd-click (ctrl-click on windows) adds or removes one task. The
    // task open in the panel is where a selection starts from.
    const select = (task: Task, e: MouseEvent) => {
        if (!e.shiftKey && !e.metaKey && !e.ctrlKey) {
            setSelected([]);
            setRangeFrom(task.id);
            panels.openTask(task);
            return;
        }

        const current = selected.length > 0 ? selected : (panels.task?.id ? [panels.task.id] : []);
        let next: number[];

        if (e.shiftKey) {
            const from = rangeFrom || current[0] || task.id;
            // a task on a squeezed day can't be seen, so a range passes over it
            const shown = list.tasks.filter(row => !row.showOn || !collapsesDay(moment(row.showOn).day(), view));
            const order = calendarOrder(shown).map(row => row.id);
            const start = order.indexOf(from);
            const end = order.indexOf(task.id);

            next = start < 0 || end < 0
                ? [task.id]
                : order.slice(Math.min(start, end), Math.max(start, end) + 1);

            setRangeFrom(from);
        } else {
            next = current.includes(task.id)
                ? current.filter(id => id !== task.id)
                : current.concat(task.id);

            setRangeFrom(task.id);
        }

        if (next.length > 1) {
            setSelected(next);
            if (panels.task) panels.close();
            return;
        }

        setSelected([]);
        const only = list.tasks.find(row => row.id === next[0]);
        if (only) panels.openTask(only);
        else panels.close();
    };

    // opening any other panel ends the selection, since the two share the panel slot
    const panelOpen = !!panels.task || !!panels.project || !!panels.goal || panels.unscheduledOpen;
    const wasPanelOpen = useRef(panelOpen);
    useEffect(() => {
        if (panelOpen && !wasPanelOpen.current) setSelected([]);
        wasPanelOpen.current = panelOpen;
    }, [panelOpen]);

    useEffect(() => {
        if (selected.length === 0) return;

        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setSelected([]);
        };

        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [selected.length]);

    // stepping through the calendar replaces rather than pushes, so the back button
    // walks out of the page instead of back through every week on the way in
    const step = (amount: number) => url.set({
        date: anchor.clone().add(amount, view === "month" ? "month" : "week").format(dateFormat),
    }, { replace: true });

    return (
        <PaletteGate>
            {/* the header and the projects strip stay put; the grid between them takes the
                height they leave and scrolls inside it */}
            <Box sx={{ pt: 1, pb: 0, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                {/* the grid runs to the edges, so the header carries its own side padding. It
                    stays one short row, so the grid gets the height */}
                <Box sx={{ px: 2, pb: 0.5 }}>
                    {/* the toolbar wraps rather than pushing the page wider, so the
                        calendar under it keeps the width it has */}
                    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                        <NewTaskButton onTask={() => panels.openTask({})} onChain={() => setChain(true)} />

                        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <IconButton size="small" sx={{ p: 0.25 }} aria-label="Previous"
                                        onClick={() => step(-1)}>
                                <ChevronLeft fontSize="small" />
                            </IconButton>
                            <Typography variant="caption" component="div" align="center"
                                        style={{ fontSize: 13 }}>
                                {rangeLabel(anchor, view)}
                            </Typography>
                            <IconButton size="small" sx={{ p: 0.25 }} aria-label="Next"
                                        onClick={() => step(1)}>
                                <ChevronRight fontSize="small" />
                            </IconButton>

                            <TinyButton color="primary"
                                        onClick={() => url.set({ date: moment().format(dateFormat) },
                                            { replace: true })}>Today</TinyButton>
                        </div>
                        <div style={{flex: 1}}></div>

                        <UnscheduledButton
                            tasks={unscheduled.tasks}
                            selectedId={panels.task?.id}
                            saving={archive.loading || moveToday.loading || toggle.loading}
                            onSelect={panels.openTask}
                            onContextMenu={menu.open}
                            onToggle={(task, checked) => toggle.callback({ id: task.id, completed: checked })}
                            onDragStart={task => setDragging({ task: task, date: "" })}
                            onDragEnd={() => setDragging(null)}
                            onViewAll={panels.openUnscheduled}
                        />

                        <Dropdown width={110} value={view} options={viewOptions}
                                  startIcon={<FilterList sx={{ fontSize: 16 }} />}
                                  onChange={value => url.set({ mode: value }, { replace: true })} />
                    </div>

                    {list.LoadingOrErrorElement}
                    {unscheduled.LoadingOrErrorElement}
                    {panels.LoadingOrErrorElement}
                    {dragError && (
                        <Typography variant="caption" color="error.main" component="div" sx={{ pb: 1 }}>
                            {dragError}
                        </Typography>
                    )}
                </Box>

                {/* the sheet under the grid only draws the rules above and below it */}
                <Paper variant="outlined" sx={{
                    borderColor: "grey.300",
                    borderRadius: 0,
                    borderLeft: 0,
                    borderRight: 0,
                    overflow: "auto",
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                }}>
                    <CalendarGrid
                        start={range.start}
                        end={range.end}
                        view={view}
                        month={anchor.month()}
                        tasks={list.tasks}
                        selectedId={panels.task?.id}
                        selectedIds={selected}
                        saving={archive.loading || moveToday.loading || create.loading || toggle.loading}
                        onCreate={(date, title) => create.callback({ date: date, title: title })}
                        onSelect={select}
                        onContextMenu={menu.open}
                        onToggle={(task, checked) => toggle.callback({ id: task.id, completed: checked })}
                        dragging={dragging}
                        onDragStart={setDragging}
                        onDragEnd={() => setDragging(null)}
                        onReorder={reorder.callback}
                        onReschedule={(task, date) => reschedule.callback({ task: task, date: date })}
                        onExpand={() => url.set({ mode: "full-week" }, { replace: true })}
                    />
                </Paper>

                <ProjectStrip
                    projects={projects.projects}
                    rangeLabel={rangeLabel(anchor, view)}
                    selectedId={panels.project?.id}
                    selectedGoalId={panels.goal?.id}
                    onSelect={panels.openProject}
                    onSelectGoal={panels.openGoal}
                    onCreate={() => panels.openProject({})}
                    showTeam={!ws.team}
                    unscheduledCount={unscheduled.tasks.length}
                    unscheduledSelected={panels.unscheduledOpen}
                    onUnscheduled={panels.toggleUnscheduled}
                />
                {projects.LoadingOrErrorElement}

                {menu.element}

                {panels.element}

                {bulk && !panelOpen && (
                    <SidePanel>
                        <BulkTaskPanel
                            tasks={selectedTasks}
                            onClose={() => setSelected([])}
                            onApplied={() => setSelected([])}
                        />
                    </SidePanel>
                )}

                {chain && (
                    <TaskChainDialog
                        onClose={() => setChain(false)}
                        onCreated={id => {
                            setChain(false);
                            panels.openTask({ id: id });
                        }}
                    />
                )}
            </Box>
        </PaletteGate>
    );
}
